import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Textarea } from './ui/textarea';
import { Alert, AlertDescription } from './ui/alert';
import { Globe, ArrowLeft, Volume2, Languages, AlertTriangle } from 'lucide-react';
import { languages } from '../mockData';
import { toast } from '../hooks/use-toast';
import { translationAPI } from '../services/api';

const Translator = () => {
  const navigate = useNavigate();
  const [selectedLanguage, setSelectedLanguage] = useState('spanish');
  const [inputText, setInputText] = useState('');
  const [translatedText, setTranslatedText] = useState('');
  const [transliteration, setTransliteration] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const lang = localStorage.getItem('selectedLanguage') || 'spanish';
    setSelectedLanguage(lang);
  }, []);

  const translateText = async () => {
    if (!inputText.trim()) {
      toast({
        title: 'Please enter text',
        description: 'Type something to translate',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    
    try {
      const data = await translationAPI.translate(inputText, selectedLanguage);
      setTranslatedText(data.translated_text);
      setTransliteration(data.transliteration || '');
      toast({
        title: 'Translation complete',
        description: 'Text has been translated',
      });
    } catch (error) {
      console.error('Translation error:', error);
      toast({
        title: 'Translation failed',
        description: 'Unable to translate at this time',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const speakTranslation = () => {
    if (!translatedText) return;
    
    if ('speechSynthesis' in window) {
      // Cancel any ongoing speech
      window.speechSynthesis.cancel();
      
      const utterance = new SpeechSynthesisUtterance(translatedText);
      
      const langCodes = {
        spanish: 'es-ES',
        french: 'fr-FR',
        german: 'de-DE',
        italian: 'it-IT',
        hindi: 'hi-IN',
        tamil: 'ta-IN',
        kannada: 'kn-IN',
        telugu: 'te-IN',
        malayalam: 'ml-IN'
      };

      utterance.lang = langCodes[selectedLanguage] || 'en-US';
      utterance.rate = 0.85;
      
      // Set appropriate voice based on gender preference
      const setVoice = () => {
        const voices = window.speechSynthesis.getVoices();
        const targetLangCode = langCodes[selectedLanguage];
        const voiceGender = localStorage.getItem('voiceGender') || 'female';
        
        if (voices.length > 0 && targetLangCode) {
          const langVoices = voices.filter(voice => 
            voice.lang.toLowerCase().startsWith(targetLangCode.substring(0, 2).toLowerCase())
          );
          
          if (langVoices.length > 0) {
            let selectedVoice = null;
            
            const femalePatterns = [
              'female', 'woman', 'samantha', 'karen', 'victoria', 'monica', 'paulina',
              'fiona', 'amelie', 'marie', 'anna', 'conchita', 'penelope', 'celine',
              'marlene', 'vicki', 'alice', 'elsa', 'aditi', 'raveena', 'priya'
            ];
            
            const malePatterns = [
              'male', 'man', 'daniel', 'thomas', 'alex', 'diego', 'jorge', 'juan',
              'carlos', 'jacques', 'mathieu', 'hans', 'luca', 'ravi', 'vijay'
            ];
            
            const patterns = voiceGender === 'female' ? femalePatterns : malePatterns;
            
            for (const pattern of patterns) {
              const regex = new RegExp(pattern, 'i');
              selectedVoice = langVoices.find(v => regex.test(v.name));
              if (selectedVoice) break;
            }
            
            utterance.voice = selectedVoice || langVoices[0];
          }
        }
        
        window.speechSynthesis.speak(utterance);
      };
      
      if (window.speechSynthesis.getVoices().length > 0) {
        setVoice();
      } else {
        window.speechSynthesis.onvoiceschanged = setVoice;
      }
    }
  };

  const selectedLang = languages.find(l => l.id === selectedLanguage);

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-sky-50">
      {/* Navigation */}
      <nav className="border-b bg-white/80 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="flex items-center space-x-2">
                <Globe className="h-8 w-8 text-amber-600" />
                <span className="text-xl font-bold text-slate-900">NomadLinguist</span>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-8">
          <Languages className="h-12 w-12 text-amber-600 mx-auto mb-4" />
          <h1 className="text-4xl font-bold text-slate-900 mb-4">Quick Translator</h1>
          <p className="text-xl text-slate-600">
            Translate English to {selectedLang?.name} for quick help while traveling
          </p>
        </div>

        {/* Disclaimer */}
        <Alert className="mb-8 border-amber-200 bg-amber-50">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-slate-700">
            <strong>Note:</strong> Translations are for conversational guidance and may not be perfect. 
            Always verify important information with locals or official sources.
          </AlertDescription>
        </Alert>

        {/* Language Selector */}
        <Card className="border-2 mb-6">
          <CardHeader>
            <CardTitle>Select Target Language</CardTitle>
            <CardDescription>Choose which language to translate to</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {languages.map((lang) => (
                <button
                  key={lang.id}
                  onClick={() => setSelectedLanguage(lang.id)}
                  className={`p-3 rounded-lg border-2 text-left hover:border-amber-400 transition-all ${
                    selectedLanguage === lang.id
                      ? 'border-amber-500 bg-amber-50'
                      : 'border-slate-200 bg-white'
                  }`}
                >
                  <p className="text-2xl mb-1">{lang.flag}</p>
                  <p className="font-semibold text-sm text-slate-900">{lang.name}</p>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Translation Interface */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Input */}
          <Card className="border-2">
            <CardHeader>
              <CardTitle className="text-lg">English</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                placeholder="Type or paste English text here..."
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                className="min-h-[200px] text-base"
              />
            </CardContent>
          </Card>

          {/* Output */}
          <Card className="border-2">
            <CardHeader>
              <CardTitle className="text-lg flex items-center justify-between">
                <span>{selectedLang?.name}</span>
                {translatedText && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={speakTranslation}
                  >
                    <Volume2 className="h-4 w-4 mr-2" />
                    Play
                  </Button>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {translatedText ? (
                <div>
                  <div className="bg-slate-50 p-4 rounded-lg border min-h-[120px] mb-4">
                    <p className="text-2xl font-bold text-amber-700 mb-2">{translatedText}</p>
                  </div>
                  {transliteration && (
                    <div className="bg-sky-50 p-3 rounded-lg border">
                      <p className="text-xs font-semibold text-slate-700 mb-1">Transliteration:</p>
                      <p className="text-slate-800 italic">{transliteration}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="min-h-[200px] flex items-center justify-center text-slate-400">
                  <p>Translation will appear here</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Translate Button */}
        <div className="flex justify-center mt-8">
          <Button
            size="lg"
            className="bg-amber-600 hover:bg-amber-700 px-12"
            onClick={translateText}
            disabled={loading}
          >
            {loading ? 'Translating...' : 'Translate'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Translator;